import { Cubit } from "blac";
import SanityService, {
  SanitySanctuaryHealthVideoData
} from "src/api/SanityService";
import VimeoService, { VimeoVideoMetaData } from "src/api/VimeoService";
import { languageAsShort, LanguageLong } from "src/constants/language";
import reportErrorSentry from "src/lib/reportErrorSentry";
import {
  VideoPlayerOptions,
  VideoTrack
} from "src/ui/components/FullVideoPlayer/FullVideoPlayerBloc";
import { translate } from "lib/blocs/TranslateionBloc";

export interface VideoData {
  thumb: string;
  width: number;
  height: number;
  fullscreen: boolean;
  inline?: boolean;
  title: string;
  videoWidth?: number;
  videoHeight?: number;
  ended?: boolean;
  src?: string;
  errorMessage?: string;
  providerErrorMessage?: string;
}

interface Srt {
  url?: string;
  path?: string;
  mimetype?: string;
}

interface VideoInitOptions {
  id: number | string;
  thumbBasicUrl: string;
  inline: boolean;
  videoData?: VideoData;
  initPlayerInline?: (
    videoOptions: VideoPlayerOptions & {
      target: HTMLVideoElement | null;
    }
  ) => void;
  initPlayerPopup?: (options: VideoPlayerOptions) => void;
  target?: HTMLVideoElement | null;
  autoplay?: boolean;
  options?: VideoPlayerOptions;
  forceRefresh?: boolean;
}

export type VideoPlayerLibInterface = {
  on: (event: string, callback: () => void) => void;
  off: (event: string, callback: () => void) => void;
  play: () => Promise<void>;
  pause: () => void;
  currentTime: () => number;
  duration: () => number;
  isFullscreen: () => boolean;
  dispose: () => void;
  isDisposed: () => boolean;
  readyState: () => number;
  networkState: () => number;
  currentSrc: () => string;
  bufferedPercent: () => number;
  error: () => MediaError | null;
  addRemoteTextTrack: (srt: Srt, b: boolean) => void;
  textTracks: () => {
    tracks_: TextTrack[];
  };
};

export default class VideoPlayerCubit extends Cubit<
  Record<string, VideoData | undefined>
> {
  portalTarget: HTMLElement | null = null;

  metadata: Record<string, VimeoVideoMetaData | undefined> = {};

  constructor() {
    super({});
  }

  public readonly getVideoSource = async (
    id: number | string,
    forceRefresh: boolean = false
  ): Promise<
    {
      src: string;
      type: "video/mp4";
    }[]
  > => {
    if (typeof id === "number") {
      return this.getVimeoVideoSource(id, forceRefresh);
    } else {
      return this.getSanityVideoSource(id, forceRefresh);
    }
  };

  public readonly getVideoTracks = (id?: number | string): VideoTrack[] => {
    if (!id) {
      return [];
    }

    const data = this.sanityMetadata[id];
    if (!data?.captions) {
      return [];
    }

    return data.captions.map(
      (caption) =>
        ({
          kind: "captions",
          src: caption.srt?.url ?? "",
          srclang: languageAsShort(caption.language) ?? "",
          label: caption.language as LanguageLong,
          format: "srt"
        }) as VideoTrack
    );
  };

  public readonly getSourcesAndTracks = async (
    id: number | string,
    forceRefresh: boolean = false
  ): Promise<{
    sources: {
      src: string;
      type: "video/mp4";
    }[];
    tracks: VideoTrack[];
  }> => {
    const sources = await this.getVideoSource(id, forceRefresh);
    const tracks = this.getVideoTracks(id);
    return { sources, tracks };
  };

  public readonly initializeVideo = async (params: VideoInitOptions) => {
    const {
      id,
      thumbBasicUrl,
      inline,
      videoData,
      initPlayerInline,
      initPlayerPopup,
      target,
      autoplay,
      forceRefresh,

      options
    } = params;
    const { sources, tracks } = await this.getSourcesAndTracks(
      id,
      forceRefresh
    );

    const ratioValue =
      videoData?.videoWidth && videoData.videoHeight
        ? videoData.videoWidth / videoData.videoHeight
        : 16 / 9;

    try {
      if (inline) {
        initPlayerInline?.({
          autoplay: autoplay,
          ratio: ratioValue,
          poster: typeof thumbBasicUrl === "string" ? thumbBasicUrl : undefined,
          sources,
          tracks: tracks,
          target: target ?? null,
          ...options
        });
      } else {
        initPlayerPopup?.({
          autoplay: true,
          ratio: ratioValue,
          poster: thumbBasicUrl,
          sources,
          tracks
        });
      }
    } catch (e) {
      reportErrorSentry(e);
    }
  };

  public readonly getSanityVideoSource = async (
    id: string,
    forceRefresh: boolean = false
  ): Promise<
    {
      src: string;
      type: "video/mp4";
    }[]
  > => {
    let inState: VideoData | null | undefined = this.state[id];
    if (forceRefresh || !inState || !inState.src) {
      inState = await this.loadSanityData(id, forceRefresh);
    }
    if (!inState || !inState.src) {
      return [];
    }

    let finalSrc = inState.src;
    if (forceRefresh) {
      const url = new URL(finalSrc);
      const timestamp = Date.now().toString();
      url.searchParams.set("cache", timestamp);
      finalSrc = url.toString();
    }

    return [
      {
        type: "video/mp4",
        src: finalSrc
      }
    ];
  };

  public readonly getVimeoVideoSource = async (
    id: number,
    forceRefresh: boolean = false
  ): Promise<
    {
      src: string;
      type: "video/mp4";
    }[]
  > => {
    try {
      const vimeoData = VimeoService.fetchVideoMetadata(id, forceRefresh);
      const { files } = await vimeoData;

      // select based on the width
      const selected =
        files?.find((file) => file.rendition === "720p") ?? files?.[0];

      if (!selected) {
        return [];
      }

      let src = selected.link;
      src = forceRefresh ? `${src}#cache=${Date.now()}` : src;

      const current = this.state[id];
      if (current?.errorMessage || current?.providerErrorMessage) {
        this.emit({
          ...this.state,
          [id]: {
            ...current,
            errorMessage: undefined,
            providerErrorMessage: undefined
          }
        });
      }

      return [
        {
          type: "video/mp4",
          src
        }
      ];
    } catch (error) {
      reportErrorSentry(error);

      this.emit({
        ...this.state,
        [id]: {
          fullscreen: false,
          thumb: "",
          width: 0,
          height: 0,
          title: "",
          errorMessage: translate("video.error", { context: "tryAgain" }),
          providerErrorMessage:
            (error as Error | undefined)?.message || undefined
        }
      });
    }
    return [];
  };

  public readonly addVideoData = (id: number | string): void => {
    if (typeof id === "number") {
      void this.loadVimeoData(id);
    } else {
      void this.loadSanityData(id);
    }
  };

  public readonly addVimeoVideo = (id: number): void => {
    void this.loadVimeoData(id);
  };

  public readonly addSanityVideo = (id: string): void => {
    void this.loadSanityData(id);
  };

  isVideoWatched = (id: number | string): boolean => {
    const endedStorage = Boolean(
      localStorage.getItem(`9am-video-${id}-watched`)
    );
    const endedState = this.state[id]?.ended ?? false;
    return endedStorage || endedState;
  };

  markVideoEnded = (id: number | string): void => {
    localStorage.setItem(`9am-video-${id}-watched`, "true");
    const current = this.state[id];

    if (!current) {
      return;
    }

    this.emit({
      ...this.state,
      [id]: { ...current, ended: true }
    });
  };

  private readonly loadVimeoData = async (id: number): Promise<void> => {
    try {
      const data = this.metadata[id] ?? (await VimeoService.getVideoMeta(id));
      this.metadata[id] = data;
      const { pictures } = data;

      const mainThumb = (pictures?.sizes ?? []).find(
        (size) => size.width === 640
      );
      if (!mainThumb) {
        return;
      }

      const newItem: VideoData = {
        fullscreen: false,
        thumb: mainThumb.link ?? "",
        width: mainThumb.width ?? 0,
        height: mainThumb.height ?? 0,
        title: data.name ?? "",
        videoWidth: data.width ?? 0,
        videoHeight: data.height ?? 0
      };

      this.emit({
        ...this.state,
        [id]: newItem
      });
    } catch (e: unknown) {
      reportErrorSentry(e);

      this.emit({
        ...this.state,
        [id]: {
          fullscreen: false,
          thumb: "",
          width: 0,
          height: 0,
          title: "",
          errorMessage: translate("video.error", { context: "tryAgain" })
        }
      });
    }
  };

  private sanityMetadata: Record<
    string,
    SanitySanctuaryHealthVideoData | null
  > = {};

  sanityData: Record<string, VideoData | null> = {};
  private readonly loadSanityData = async (
    id: string,
    forceRefresh: boolean = false
  ): Promise<VideoData | null> => {
    try {
      const data = forceRefresh
        ? await SanityService.resolveVideoById(id)
        : (this.sanityMetadata[id] ??
          (await SanityService.resolveVideoById(id)));
      this.sanityMetadata[id] = data;

      if (!data || Array.isArray(data)) {
        throw new Error("Could not load sanity video data");
      }

      const newItem: VideoData = {
        fullscreen: false,
        thumb: data?.photoUrl ?? "",
        width: 0,
        height: 0,
        title: data?.title ?? "",
        videoWidth: 0,
        videoHeight: 0,
        src: data?.landscapeVideo?.path
      };

      this.emit({
        ...this.state,
        [id]: newItem
      });
      return newItem;
    } catch (e: unknown) {
      reportErrorSentry(e);

      this.emit({
        ...this.state,
        [id]: {
          fullscreen: false,
          thumb: "",
          width: 0,
          height: 0,
          title: "",
          errorMessage: translate("video.error", { context: "tryAgain" })
        }
      });
    }
    return null;
  };

  getVideoIdFromSrc = (src: string): number | string | null => {
    const vimeoId = VimeoService.extractIdFromUrl(src);
    const sanityId = SanityService.checkIsVideoId(src);
    return vimeoId ?? sanityId;
  };
}
